import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import PageTransition from "./components/PageTransition";
import ScrollToTop from "./components/ScrollToTop";
import { detectLanguageFromPath } from "@/lib/routes";
import { queryClient } from "@/lib/queryClient";

import Index from "./pages/Index";
const AboutMufeed = lazy(() => import("./pages/AboutMufeed"));
const OurCompanies = lazy(() => import("./pages/OurCompanies"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const InvestorRelations = lazy(() => import("./pages/InvestorRelations"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const MediaCenter = lazy(() => import("./pages/MediaCenter"));
const NewsDetail = lazy(() => import("./pages/NewsDetail"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminNews = lazy(() => import("./pages/admin/AdminNews"));
const AdminNewsForm = lazy(() => import("./pages/admin/AdminNewsForm"));
const AdminProjects = lazy(() => import("./pages/admin/AdminProjects"));
const AdminProjectForm = lazy(() => import("./pages/admin/AdminProjectForm"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const AdminSuppliers = lazy(() => import("./pages/admin/AdminSuppliers"));
const AdminApplications = lazy(() => import("./pages/admin/AdminApplications"));
const AdminSubmissionDetail = lazy(() => import("./pages/admin/AdminSubmissionDetail"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const SuppliersRegistration = lazy(() => import("./pages/SuppliersRegistration"));
const JobApplication = lazy(() => import("./pages/JobApplication"));

const LanguageSync = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = detectLanguageFromPath(location.pathname);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [location.pathname, i18n]);

  return null;
};

const PageLoader = () => (
  <div style={{ minHeight: '100vh', background: '#050505' }} />
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <>
      <ScrollToTop />
      <LanguageSync />
      <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/about-us" element={<PageTransition><AboutMufeed /></PageTransition>} />
        <Route path="/our-companies" element={<PageTransition><OurCompanies /></PageTransition>} />
        <Route path="/projects" element={<PageTransition><Projects /></PageTransition>} />
        <Route path="/projects/:slug" element={<PageTransition><ProjectDetail /></PageTransition>} />
        <Route path="/investors-relation" element={<PageTransition><InvestorRelations /></PageTransition>} />
        <Route path="/media-center" element={<PageTransition><MediaCenter /></PageTransition>} />
        <Route path="/media-center/:slug" element={<PageTransition><NewsDetail /></PageTransition>} />
        <Route path="/contact-us" element={<PageTransition><ContactUs /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><TermsConditions /></PageTransition>} />

        <Route path="/ar" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/ar/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/ar/من-نحن" element={<PageTransition><AboutMufeed /></PageTransition>} />
        <Route path="/ar/شركاتنا" element={<PageTransition><OurCompanies /></PageTransition>} />
        <Route path="/ar/مشاريعنا" element={<PageTransition><Projects /></PageTransition>} />
        <Route path="/ar/مشاريعنا/:slug" element={<PageTransition><ProjectDetail /></PageTransition>} />
        <Route path="/ar/علاقات-المستثمرين" element={<PageTransition><InvestorRelations /></PageTransition>} />
        <Route path="/ar/المركز-الإعلامي" element={<PageTransition><MediaCenter /></PageTransition>} />
        <Route path="/ar/المركز-الإعلامي/:slug" element={<PageTransition><NewsDetail /></PageTransition>} />
        <Route path="/ar/اتصل-بنا" element={<PageTransition><ContactUs /></PageTransition>} />
        <Route path="/ar/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="/ar/terms" element={<PageTransition><TermsConditions /></PageTransition>} />

        <Route path="/suppliers-registration" element={<PageTransition><SuppliersRegistration /></PageTransition>} />
        <Route path="/ar/تسجيل-الموردين" element={<PageTransition><SuppliersRegistration /></PageTransition>} />
        <Route path="/careers/apply" element={<PageTransition><JobApplication /></PageTransition>} />
        <Route path="/ar/التوظيف/تقديم" element={<PageTransition><JobApplication /></PageTransition>} />

        <Route path="/about" element={<Navigate to="/about-us" replace />} />
        <Route path="/companies" element={<Navigate to="/our-companies" replace />} />
        <Route path="/investors" element={<Navigate to="/investors-relation" replace />} />
        <Route path="/media" element={<Navigate to="/media-center" replace />} />
        <Route path="/media/:slug" element={<OldMediaRedirect />} />
        <Route path="/contact" element={<Navigate to="/contact-us" replace />} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="news" element={<AdminNews />} />
          <Route path="news/new" element={<AdminNewsForm />} />
          <Route path="news/:id/edit" element={<AdminNewsForm />} />
          <Route path="projects" element={<AdminProjects />} />
          <Route path="projects/new" element={<AdminProjectForm />} />
          <Route path="projects/:id/edit" element={<AdminProjectForm />} />
          <Route path="subscribers" element={<AdminSubscribers />} />
          <Route path="suppliers" element={<AdminSuppliers />} />
          <Route path="suppliers/:id" element={<AdminSubmissionDetail kind="supplier" />} />
          <Route path="applications" element={<AdminApplications />} />
          <Route path="applications/:id" element={<AdminSubmissionDetail kind="application" />} />
          <Route path="reports" element={<AdminReports />} />
        </Route>

        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
      </Suspense>
      </AnimatePresence>
    </>
  );
};

const OldMediaRedirect = () => {
  const location = useLocation();
  const slug = location.pathname.replace('/media/', '');
  return <Navigate to={`/media-center/${slug}`} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnimatedRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
